import React, { useState, useEffect } from "react";
import { ArrowLeft, AlertCircle, Shield, User, Building } from "lucide-react";
import { usePaymentApi } from "../hooks/use-payment-api";
import messageHandler from "../../../../utils/messageHandler";
import logger from "../../../../utils/logger";
import { PhoneInput } from "../../../../components/ui/phone-input";


const MpesaPaybillForm = ({ onPrevious, onComplete, existingData = null }) => {
  const { loading, error, fetchBusinessCategories, submitPaymentSetup } = usePaymentApi();
  const [businessCategories, setBusinessCategories] = useState([]);
  const [formData, setFormData] = useState({
    paymentMethod: "paybill",
    paybillNumber: existingData?.paybillNumber || "",
    paybillShortcode: existingData?.paybillShortcode || "",
    accountName: existingData?.accountName || "",
    businessRegistrationNumber: existingData?.businessRegistrationNumber || "",
    businessLicenseNumber: existingData?.businessLicenseNumber || "",
    businessCategory: existingData?.businessCategory || "",
    taxPinNumber: existingData?.taxPinNumber || "",
    businessPhoneNumber: existingData?.businessPhoneNumber || "",
    businessEmailAddress: existingData?.businessEmailAddress || "",
    businessOperatingHours: existingData?.businessOperatingHours || "",
    contactPersonName: existingData?.contactPersonName || "",
    contactPersonPhone: existingData?.contactPersonPhone || "",
    contactPersonIdNumber: existingData?.contactPersonIdNumber || "",
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const categoriesData = await fetchBusinessCategories();
        if (categoriesData && Array.isArray(categoriesData)) {
          setBusinessCategories(categoriesData);
        }
      } catch (error) {
        logger.error("Error loading business categories:", error);
        messageHandler.showApiMessage(error, "load business categories");
        setBusinessCategories([]);
      }
    };

    loadData();
  }, []);

  const handleSubmit = async () => {
    try {
      const result = await submitPaymentSetup(formData);
      if (result) {
        messageHandler.showApiMessage(result, "payment setup");
        onComplete();
      } else {
        messageHandler.showApiMessage({ message: "Payment setup failed" }, "payment setup");
      }
    } catch (error) {
      messageHandler.showApiMessage(error, "payment setup");
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      {/* Header */}
      <div className="px-6 py-4 border-b">
        <h1 className="text-xl font-semibold">Payment Setup</h1>
        <div className="w-full bg-gray-200 h-1 mt-2">
          <div className="bg-black h-1 w-full"></div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="mb-6">
          <h2 className="text-lg font-medium mb-2">Payment Setup</h2>
          <p className="text-gray-600">Set up your payment method for receiving payments</p>
        </div>

        <div className="mb-6">
          <h3 className="font-medium mb-2">Payment Method</h3>
          <p className="text-gray-600 mb-4">
            Select how you would like to receive payments. This will be synced with our internal system.
          </p>

          {/* Selected Method Display */}
          <div className="border rounded-lg p-4 bg-green-50 mb-6">
            <div className="flex items-center">
              <input type="radio" checked readOnly className="mr-3" />
              <div className="flex items-center">
                <div className="w-8 h-8 bg-green-100 rounded flex items-center justify-center mr-3">
                  <div className="w-4 h-4 bg-green-500 rounded"></div>
                </div>
                <div>
                  <div className="font-medium">M-Pesa Paybill</div>
                  <div className="text-sm text-gray-600">Receive payments via M-Pesa Paybill number</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* M-Pesa Paybill Details */}
        <div className="mb-6">
          <div className="flex items-center mb-2">
            <Shield className="w-5 h-5 mr-2" />
            <h3 className="font-medium">M-Pesa Paybill Details</h3>
          </div>
          <p className="text-gray-600 mb-4">Provide your M-Pesa Paybill information for payment processing</p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
              <p className="text-sm text-blue-800">
                Ensure your Paybill number is active and properly configured for receiving payments.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Paybill Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Enter your Paybill number"
                className="w-full border rounded-lg px-3 py-2 bg-gray-50"
                value={formData.paybillNumber}
                onChange={e => handleInputChange("paybillNumber", e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Paybill Shortcode</label>
              <input
                type="text"
                placeholder="Enter shortcode (if different)"
                className="w-full border rounded-lg px-3 py-2 bg-gray-50"
                value={formData.paybillShortcode}
                onChange={e => handleInputChange("paybillShortcode", e.target.value)}
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Account Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Enter account holder name"
              className="w-full border rounded-lg px-3 py-2 bg-gray-50"
              value={formData.accountName}
              onChange={e => handleInputChange("accountName", e.target.value)}
            />
          </div>

          <div className="mb-6">
            <label className="flex items-center">
              <input type="checkbox" className="mr-2" />
              <span className="text-sm">I confirm that this Paybill is active and operational</span>
            </label>
          </div>
        </div>

        {/* Business Information */}
        <div className="mb-6">
          <div className="flex items-center mb-2">
            <Building className="w-5 h-5 mr-2" />
            <h3 className="font-medium">Business Information</h3>
          </div>
          <p className="text-gray-600 mb-4">Business registration and compliance details</p>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-2">Business Registration Number</label>
              <input
                type="text"
                placeholder="Enter business registration number"
                className="w-full border rounded-lg px-3 py-2 bg-gray-50"
                value={formData.businessRegistrationNumber}
                onChange={e => handleInputChange("businessRegistrationNumber", e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Business License Number</label>
              <input
                type="text"
                placeholder="Enter business license number"
                className="w-full border rounded-lg px-3 py-2 bg-gray-50"
                value={formData.businessLicenseNumber}
                onChange={e => handleInputChange("businessLicenseNumber", e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Business Category <span className="text-red-500">*</span>
              </label>
              <select
                className="w-full border rounded-lg px-3 py-2 bg-gray-50"
                value={formData.businessCategory}
                onChange={e => handleInputChange("businessCategory", e.target.value)}
              >
                <option value="">Select business category</option>
                {Array.isArray(businessCategories) &&
                  businessCategories.map(category => (
                    <option key={category.id} value={category.code}>
                      {category.name}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Tax PIN Number</label>
              <input
                type="text"
                placeholder="Enter KRA PIN number"
                className="w-full border rounded-lg px-3 py-2 bg-gray-50"
                value={formData.taxPinNumber}
                onChange={e => handleInputChange("taxPinNumber", e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Business Phone Number <span className="text-red-500">*</span>
              </label>
              <PhoneInput
                defaultCountry="ke"
                value={formData.businessPhoneNumber}
                onChange={value => handleInputChange("businessPhoneNumber", value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Business Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                placeholder="Enter business email address"
                className="w-full border rounded-lg px-3 py-2 bg-gray-50"
                value={formData.businessEmailAddress}
                onChange={e => handleInputChange("businessEmailAddress", e.target.value)}
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Business Operating Hours</label>
            <input
              type="text"
              placeholder="e.g., Monday-Friday 8:00 AM - 6:00 PM"
              className="w-full border rounded-lg px-3 py-2 bg-gray-50"
              value={formData.businessOperatingHours}
              onChange={e => handleInputChange("businessOperatingHours", e.target.value)}
            />
          </div>
        </div>

        {/* Contact Person Details */}
        <div className="mb-6">
          <div className="flex items-center mb-2">
            <User className="w-5 h-5 mr-2" />
            <h3 className="font-medium">Contact Person Details</h3>
          </div>
          <p className="text-gray-600 mb-4">Primary contact for payment and verification purposes</p>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Contact Person Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Enter contact person full name"
                className="w-full border rounded-lg px-3 py-2 bg-gray-50"
                value={formData.contactPersonName}
                onChange={e => handleInputChange("contactPersonName", e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Contact Person Phone <span className="text-red-500">*</span>
              </label>
              <PhoneInput
                defaultCountry="ke"
                value={formData.contactPersonPhone}
                onChange={value => handleInputChange("contactPersonPhone", value)}
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Contact Person ID/Passport Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Enter ID or passport number"
              className="w-full border rounded-lg px-3 py-2 bg-gray-50"
              value={formData.contactPersonIdNumber}
              onChange={e => handleInputChange("contactPersonIdNumber", e.target.value)}
            />
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <button onClick={onPrevious} className="flex items-center text-gray-600 hover:text-gray-800">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 flex items-center disabled:opacity-50"
          >
            {loading ? "Processing..." : "Complete Setup"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MpesaPaybillForm;
